// UserList.tsx - Updated with portal-based dropdown to prevent overflow clipping
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './FocalPointsManagement.css'; 
import { FocalPointsApiService } from './apiService';
import { User, Company, UserType, FocalPointsManagementProps } from './types';
import { UserDetailsModal } from './UserDetailsModal';
import { UserDeleteConfirmationModal } from './UserDeleteConfirmationModal';
import '../EnterpriseTable.css'; 
import '../companyManagement/CompanyListOverrides.css';
import '../companyManagement/CompanyHelpPanel.css';
import '../companyManagement/ViewDetailsModal.css';

interface UserListProps extends FocalPointsManagementProps {
  users: User[];
  isLoading: boolean;
  selectedUserType: UserType;
  selectedCompany: Company | null;
  onEdit: (user: User) => void;
  onCreateNew: (userType: UserType) => void;
  onChangeSelection: () => void;
  onRefresh: () => void;
}

interface DropdownState {
  isOpen: boolean;
  userId: string | null;
  position: { top: number; left: number; openUpward: boolean } | null;
}

export const UserList: React.FC<UserListProps> = ({
  users,
  isLoading,
  selectedUserType,
  selectedCompany,
  getAccessToken,
  onStatusUpdate,
  onEdit,
  onCreateNew,
  onChangeSelection,
  onRefresh
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [helpVisible, setHelpVisible] = useState(true);
  const [showActionHint, setShowActionHint] = useState(false);
  const [apiService] = useState(() => new FocalPointsApiService(getAccessToken));
  const [dropdown, setDropdown] = useState<DropdownState>({ isOpen: false, userId: null, position: null });
  const [, setResendingInvitation] = useState<string | null>(null);
  const [syncingStatus, setSyncingStatus] = useState<Set<string>>(new Set());
  const [, setLastSyncTime] = useState<Date | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // View Details Modal State
  const [viewDetailsModal, setViewDetailsModal] = useState({
    isOpen: false,
    user: null as User | null
  });

  // Delete Confirmation Modal State
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    user: null as User | null
  });
  
  // Refs for dropdown management
  const dropdownButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const portalDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on scroll or resize (portal is position:fixed)
  useEffect(() => {
    if (!dropdown.isOpen) return;
    
    const closeDropdown = () => {
      setDropdown({ isOpen: false, userId: null, position: null });
    };

    window.addEventListener('scroll', closeDropdown, true);
    window.addEventListener('resize', closeDropdown);
    return () => {
      window.removeEventListener('scroll', closeDropdown, true);
      window.removeEventListener('resize', closeDropdown);
    };
  }, [dropdown.isOpen]);

  // Helper function to check if user is external
  const isUserExternal = useCallback((user: User): boolean => {
    if (!user.userEmail || !selectedCompany || selectedUserType !== 'company') return false;
    
    const emailDomain = user.userEmail.split('@')[1]?.toLowerCase();
    const companyNameLower = selectedCompany.companyName.toLowerCase().replace(/\s+/g, '');
    
    return emailDomain ? !emailDomain.includes(companyNameLower) : false;
  }, [selectedCompany, selectedUserType]);

  // Check if user has an AD guest invitation (sent or accepted)
  const isADGuestUser = useCallback((user: User): boolean => {
    if (!isUserExternal(user)) return false;
    // @ts-ignore - ad_invitation_sent may not be in type definition yet
    return !!user.ad_invitation_sent;
  }, [isUserExternal]);

  // Get AD invitation status from user data (from database)
  const getADInvitationStatus = useCallback((user: User): 'sent' | 'accepted' | 'pending' | 'expired' | 'not_sent' => {
    if (!isUserExternal(user)) return 'not_sent';
    
    // Read actual status from database (passed in User object)
    // @ts-ignore - ad_invitation_status may not be in type definition yet
    const dbStatus = user.ad_invitation_status?.toLowerCase();
    
    // @ts-ignore
    if (!user.ad_invitation_sent) {
      return 'not_sent';
    }
    
    // Map database status to frontend status
    switch (dbStatus) {
      case 'accepted':
        return 'accepted';
      case 'pendingacceptance':
        // @ts-ignore - check if expired (7 days)
        if (user.ad_invitation_sent_at) {
          // @ts-ignore
          const sentDate = new Date(user.ad_invitation_sent_at);
          const now = new Date();
          const daysSinceSent = (now.getTime() - sentDate.getTime()) / (1000 * 60 * 60 * 24);
          
          if (daysSinceSent > 7) {
            return 'expired';
          }
        }
        return 'sent';
      case 'expired':
        return 'expired';
      default:
        return 'pending';
    }
  }, [isUserExternal]);

  // Auto-sync all pending invitation statuses
  const syncAllPendingInvitations = useCallback(async (silent: boolean = true) => {
    const pendingUsers = users.filter(u => {
      const status = getADInvitationStatus(u);
      return status === 'sent' || status === 'pending';
    });

    if (pendingUsers.length === 0) return;

    if (!silent) {
      console.log(`🔄 Syncing ${pendingUsers.length} pending invitations...`);
    }

    let syncedCount = 0;
    let updatedCount = 0;

    for (const user of pendingUsers) {
      try {
        const result = await apiService.syncADInvitationStatus(user.userEmail);
        if (result.success) {
          syncedCount++;
          // Check if status actually changed
          if (result.data?.status !== 'PendingAcceptance') {
            updatedCount++;
          }
        }
      } catch (error) {
        console.error(`Failed to sync ${user.userEmail}:`, error);
      }
    }

    if (updatedCount > 0) {
      setLastSyncTime(new Date());
      onRefresh();
      
      if (!silent) {
        onStatusUpdate({
          type: 'success',
          message: `✅ Refreshed ${syncedCount} invitations, ${updatedCount} status changes detected`
        });
      }
    } else if (!silent && syncedCount > 0) {
      onStatusUpdate({
        type: 'success',
        message: `ℹ️ All ${syncedCount} invitations still pending`
      });
    }
  }, [users, apiService, getADInvitationStatus, onRefresh, onStatusUpdate]);

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) {
      return users;
    }

    const term = searchTerm.toLowerCase().trim();
    
    return users.filter(user => 
      user.userName.toLowerCase().includes(term) ||
      user.userEmail.toLowerCase().includes(term) ||
      user.userPhoneNo.toLowerCase().includes(term) ||
      user.nationality.toLowerCase().includes(term) ||
      user.role.toLowerCase().includes(term) ||
      (user.functionName && user.functionName.toLowerCase().includes(term))
    );
  }, [users, searchTerm]);

  // Calculate statistics including AD invitation stats
  const stats = useMemo(() => {
    const externalUsers = users.filter(u => isUserExternal(u));
    const withInvitations = externalUsers.filter(u => {
      const status = getADInvitationStatus(u);
      return status !== 'not_sent';
    });
    
    return {
      total: users.length,
      approvers: users.filter(u => u.role === 'approver').length,
      blacklisted: users.filter(u => u.isBlacklisted).length,
      external: externalUsers.length,
      invitationsSent: withInvitations.length
    };
  }, [users, isUserExternal, getADInvitationStatus]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdown.isOpen || !dropdown.userId) return;

      const buttonElement = dropdownButtonRefs.current.get(dropdown.userId);
      const portalElement = portalDropdownRef.current;
      
      if (buttonElement && !buttonElement.contains(event.target as Node) &&
          (!portalElement || !portalElement.contains(event.target as Node))) {
        setDropdown({ isOpen: false, userId: null, position: null });
      }
    };

    if (dropdown.isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [dropdown.isOpen, dropdown.userId]);

  // Auto-sync invitation statuses for pending/sent users on mount
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      syncAllPendingInvitations(true);
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [syncAllPendingInvitations]);

  // Auto-sync pending invitations every 60 seconds
  useEffect(() => {
    const pendingUsers = users.filter(u => {
      const status = getADInvitationStatus(u);
      return status === 'sent' || status === 'pending';
    });

    // Only set up interval if there are pending users
    if (pendingUsers.length === 0) return;

    const intervalId = setInterval(() => {
      syncAllPendingInvitations(true);
    }, 60000); // 60 seconds

    return () => clearInterval(intervalId);
  }, [users, getADInvitationStatus, syncAllPendingInvitations]);

  // Toggle dropdown - calculates position for portal rendering
  const toggleDropdown = (userId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    if (dropdown.isOpen && dropdown.userId === userId) {
      setDropdown({ isOpen: false, userId: null, position: null });
    } else {
      const buttonEl = dropdownButtonRefs.current.get(userId);
      if (buttonEl) {
        const rect = buttonEl.getBoundingClientRect();
        const menuHeight = 200; // approximate dropdown menu height
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUpward = spaceBelow < menuHeight;

        setDropdown({
          isOpen: true,
          userId,
          position: {
            top: openUpward ? rect.top : rect.bottom + 4,
            left: rect.right,
            openUpward
          }
        });
      }
    }
  };

  // Handle resend AD invitation
  const handleResendInvitation = async (user: User) => {
    setDropdown({ isOpen: false, userId: null, position: null });
    setResendingInvitation(user.userId.toString());

    const result = await apiService.sendADInvitation({
      email: user.userEmail,
      displayName: user.userName,
      sendInvitationMessage: true,
      inviteRedirectUrl: 'https://myapp.azurewebsites.net'
    });

    setResendingInvitation(null);

    if (result.success) {
      onStatusUpdate({
        type: 'success',
        message: `✅ Azure AD invitation resent to ${user.userEmail}`
      });
      onRefresh();
    } else {
      onStatusUpdate({
        type: 'error',
        message: result.message || 'Failed to resend invitation'
      });
    }
  };

  // Handle send initial invitation (for users without invitation)
  const handleSendInvitation = async (user: User) => {
    setDropdown({ isOpen: false, userId: null, position: null });
    setResendingInvitation(user.userId.toString());

    const result = await apiService.sendADInvitation({
      email: user.userEmail,
      displayName: user.userName,
      sendInvitationMessage: true,
      inviteRedirectUrl: 'https://myapp.azurewebsites.net'
    });

    setResendingInvitation(null);

    if (result.success) {
      onStatusUpdate({
        type: 'success',
        message: `✅ Azure AD invitation sent to ${user.userEmail}`
      });
      onRefresh();
    } else {
      onStatusUpdate({
        type: 'error',
        message: result.message || 'Failed to send invitation'
      });
    }
  };

  // Handle sync invitation status for a specific user
  const handleSyncInvitationStatus = async (user: User) => {
    setDropdown({ isOpen: false, userId: null, position: null });
    setSyncingStatus(prev => new Set(prev).add(user.userEmail));

    const result = await apiService.syncADInvitationStatus(user.userEmail);

    setSyncingStatus(prev => {
      const newSet = new Set(prev);
      newSet.delete(user.userEmail);
      return newSet;
    });

    if (result.success) {
      onStatusUpdate({
        type: 'success',
        message: `✅ Invitation status refreshed for ${user.userEmail}`
      });
      onRefresh();
    } else {
      onStatusUpdate({
        type: 'error',
        message: result.message || 'Failed to refresh status'
      });
    }
  };

  // Handle row click - opens view details
  const handleRowClick = (user: User) => {
    setViewDetailsModal({
      isOpen: true,
      user: user
    });
  };

  // Handle view details
  const handleViewDetails = (user: User) => {
    setDropdown({ isOpen: false, userId: null, position: null });
    setViewDetailsModal({
      isOpen: true,
      user: user
    });
  };

  // Handle close view details modal
  const handleCloseViewDetails = () => {
    setViewDetailsModal({
      isOpen: false,
      user: null
    });
  };

  // Handle delete user
  const handleDelete = (user: User) => {
    setDropdown({ isOpen: false, userId: null, position: null });
    setDeleteModal({
      isOpen: true,
      user: user
    });
  };

  // Handle delete confirmation - UPDATED to also remove AD guest user
  const handleConfirmDelete = async () => {
    if (!deleteModal.user) return;

    const userToDelete = deleteModal.user;
    const isGuest = isADGuestUser(userToDelete);
    
    setIsDeleting(true);

    // Step 1: If the user is an AD guest, remove them from Entra ID first
    let adRemovalResult = null;
    if (isGuest) {
      onStatusUpdate({
        type: 'success',
        message: `Removing ${userToDelete.userEmail} from Entra ID...`
      });

      // @ts-ignore - ad_user_id may not be in type definition yet
      adRemovalResult = await apiService.removeADGuestUser(
        userToDelete.userEmail,
        // @ts-ignore
        userToDelete.ad_user_id || null
      );

      if (!adRemovalResult.success) {
        // AD removal failed - warn but still allow app-level deletion
        onStatusUpdate({
          type: 'error',
          message: `⚠️ Warning: Could not remove guest from Entra ID (${adRemovalResult.message}). Proceeding with app-level deletion.`
        });
      }
    }

    // Step 2: Delete the user from the application database
    const result = await apiService.deleteUser(userToDelete);
    
    setDeleteModal({ isOpen: false, user: null });
    setIsDeleting(false);
    
    if (result.success) {
      // Build success message based on what happened
      let successMessage = `User "${userToDelete.userName}" has been successfully deleted and their application access has been revoked.`;
      
      if (isGuest && adRemovalResult?.success && adRemovalResult.data?.adUserRemoved) {
        successMessage += ` Their Entra ID guest account has also been removed.`;
      } else if (isGuest && adRemovalResult?.success && !adRemovalResult.data?.adUserRemoved) {
        if (adRemovalResult.data?.reason === 'native_user') {
          successMessage += ` Note: Their Entra ID account is a native account and was not removed.`;
        } else if (adRemovalResult.data?.reason === 'not_found_in_ad') {
          successMessage += ` Note: No matching guest account was found in Entra ID.`;
        }
      }

      onStatusUpdate({
        type: 'success',
        message: successMessage
      });
      onRefresh();
    } else {
      onStatusUpdate({
        type: 'error',
        message: result.message || 'Failed to delete user'
      });
    }
  };

  // Handle delete cancellation
  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, user: null });
  };

  // Handle edit user
  const handleEdit = (user: User) => {
    setDropdown({ isOpen: false, userId: null, position: null });
    onEdit(user);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
  };

  // Auto-dismiss action hint after 6 seconds or on first dropdown open
  useEffect(() => {
    if (!showActionHint) return;
    const timer = setTimeout(() => setShowActionHint(false), 6000);
    return () => clearTimeout(timer);
  }, [showActionHint]);

  useEffect(() => {
    if (dropdown.isOpen) setShowActionHint(false);
  }, [dropdown.isOpen]);

  if (isLoading) {
    return (
      <div className="loading">
        <span className="spinner"></span>
        Loading users...
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="company-management">
        <div className="no-companies">
          <div className="no-data-icon">👤</div>
          <h3>No Users Found</h3>
          <p>
            {selectedUserType === 'company' 
              ? "This company doesn't have any users yet. Add the first one to grant application access!"
              : "No gate users found. Add the first one!"
            }
          </p>
          <button 
            onClick={() => onCreateNew(selectedUserType)} 
            className="create-first-btn"
          >
            Add First {selectedUserType === 'company' ? 'Company' : 'Gate'} User
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="company-management">
      <div className={`cm-layout ${!helpVisible ? 'help-collapsed' : ''}`}>
        <div className="cm-main">
      <div className="companies-container">
        {/* Toolbar — matching CompanyList/FunctionList */}
        <div className="table-toolbar">
          <div className="toolbar-left">
            <span className="record-count">{users.length} users</span>
          </div>
          <div className="toolbar-right">
            <div className="search-field">
              <svg className="search-icon-svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button onClick={clearSearch} className="clear-search-btn" title="Clear search">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
            <button onClick={() => onCreateNew(selectedUserType)} className="create-company-btn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>Add {selectedUserType === 'company' ? 'Company' : 'Gate'} User</span>
            </button>
          </div>
        </div>

        {searchTerm && (
          <div className="search-results-bar">
            Showing {filteredUsers.length} of {users.length} results for &ldquo;<strong>{searchTerm}</strong>&rdquo;
            <button onClick={clearSearch} className="clear-filter-btn">Clear filter</button>
          </div>
        )}

        <div className="companies-table-container">
          <table className="enterprise-table">
            <thead>
              <tr>
                <th>User Name</th>
                <th>Email</th>
                {selectedUserType === 'company' && <th>Phone Number</th>}
                <th className="th-actions"></th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={selectedUserType === 'company' ? 4 : 3} className="no-results">
                    <div className="no-results-content">
                      <span className="no-results-icon">🔍</span>
                      <p>No users found matching "{searchTerm}"</p>
                      <button onClick={clearSearch} className="clear-search-link">
                        Clear search to see all users
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user, rowIndex) => {
                  //const adStatus = getADInvitationStatus(user);
                  
                  
                  return (
                    <tr
                      key={user.userId}
                      className="clickable-row"
                      onClick={() => handleRowClick(user)}
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleRowClick(user); }}
                    >
                      <td className="td-name">
                        <div className="name-details">
                          <span className="org-name-text">
                            {user.userName}
                            {user.isBlacklisted && (
                              <span
                                className="blacklisted-badge"
                                title="This user is blacklisted"
                                style={{
                                  marginLeft: '8px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '3px',
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  color: '#dc2626',
                                  background: '#fef2f2',
                                  border: '1px solid #fecaca',
                                  borderRadius: '4px',
                                  padding: '1px 6px',
                                  letterSpacing: '0.02em',
                                  lineHeight: '16px',
                                  verticalAlign: 'middle',
                                  textTransform: 'uppercase',
                                }}
                              >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="12" cy="12" r="10" />
                                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                                </svg>
                                Blacklisted
                              </span>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="td-contact">
                        <div className="contact-info">
                          <span className="contact-email">{user.userEmail}</span>
                        </div>
                      </td>
                      {selectedUserType === 'company' && (
                        <td>
                          <span style={{ color: '#374151', fontSize: '13px' }}>{user.userPhoneNo || '—'}</span>
                        </td>
                      )}
                      <td className="td-actions" onClick={(e) => e.stopPropagation()}>
                        <div className="users-action-dropdown">
                          {rowIndex === 0 && showActionHint && (
                            <div className="action-hint-tooltip" onClick={() => setShowActionHint(false)}>
                              <span>Click to view, edit, or manage this user</span>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                              </svg>
                            </div>
                          )}
                          <button
                            ref={(el) => {
                              if (el) {
                                dropdownButtonRefs.current.set(user.userId.toString(), el);
                              } else {
                                dropdownButtonRefs.current.delete(user.userId.toString());
                              }
                            }}
                            onClick={(e) => toggleDropdown(user.userId.toString(), e)}
                            className={`users-action-trigger ${rowIndex === 0 && showActionHint ? 'hint-pulse' : ''}`}
                            title="More actions"
                            type="button"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="5" r="2" />
                              <circle cx="12" cy="12" r="2" />
                              <circle cx="12" cy="19" r="2" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Stats section */}
        <div className="table-footer">
          <div className="table-stats">
            <span className="stat-item">
              <strong>{stats.total}</strong> Total Users
            </span>
            <span className="stat-item">
              <strong>{stats.approvers}</strong> Approvers
            </span>
            <span className="stat-item">
              <strong>{stats.blacklisted}</strong> Blacklisted
            </span>
            {selectedUserType === 'company' && stats.external > 0 && (
              <>
                <span className="stat-item">
                  <strong>{stats.external}</strong> External
                </span>
                <span className="stat-item">
                  <strong>{stats.invitationsSent}</strong> AD Invitations
                </span>
              </>
            )}
          </div>
        </div>
      </div>
        </div>

        {/* Right: Help Guide — animated collapse */}
        <div
          className="cm-help-right"
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            width: helpVisible ? 310 : 44,
            padding: '28px 0 32px 0',
            transition: 'width 0.3s ease',
            overflow: 'hidden',
          }}
        >
          <div
            className="cm-help-content"
            style={{
              flex: helpVisible ? 1 : '0 0 0px',
              opacity: helpVisible ? 1 : 0,
              overflow: 'hidden',
              transition: 'opacity 0.25s ease, flex 0.3s ease',
              pointerEvents: helpVisible ? 'auto' : 'none',
              minWidth: 0,
              padding: helpVisible ? '0 16px 0 28px' : '0',
            }}
          >
            <div className="cm-help-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <p className="cm-help-title">User Management</p>
            <p className="cm-help-text">
              {selectedUserType === 'company' 
                ? `Manage users for ${selectedCompany?.companyName || 'this company'}. Company users can be assigned as approvers or reviewers within divisions.`
                : 'Manage gate-level users who handle security checks and gate access control.'
              }
            </p>
            <p className="cm-help-subtitle">User Roles</p>
            <p className="cm-help-text">
              Users can be approvers (approve/reject gate pass requests) or reviewers (review before approval). Roles are assigned at the division level.
            </p>
            <p className="cm-help-subtitle">External Users</p>
            <p className="cm-help-text">
              External users (from different email domains) can receive Azure AD guest invitations for secure access.
            </p>
            <p className="cm-help-subtitle">Quick Actions</p>
            <p className="cm-help-text">
              Click any row to view full details with action buttons inside. Or use the three-dot menu for quick access to edit, send AD invitations, or remove a user.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setHelpVisible(!helpVisible)}
            aria-label={helpVisible ? 'Hide Guide' : 'Show Guide'}
            title={helpVisible ? 'Hide Guide' : 'Show Guide'}
            style={helpVisible ? {
              display: 'flex', alignItems: 'center', gap: 6,
              writingMode: 'vertical-rl', textOrientation: 'mixed',
              background: '#f5f5f5', border: '1px solid #e0e0e0', borderRight: 'none',
              borderRadius: '6px 0 0 6px', padding: '12px 7px',
              fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer',
              letterSpacing: '0.02em', flexShrink: 0, alignSelf: 'flex-start',
              marginTop: 36, zIndex: 2, transition: 'background 0.15s ease',
            } : {
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 34, height: 34, borderRadius: '50%',
              border: '1.5px solid #d4d4d4', background: '#ffffff', cursor: 'pointer',
              flexShrink: 0, margin: '6px auto 0 auto', padding: 0,
              transition: 'background 0.15s ease, border-color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              if (helpVisible) { e.currentTarget.style.background = '#ebebeb'; }
              else { e.currentTarget.style.borderColor = '#F3694A'; e.currentTarget.style.background = '#fef7f5'; }
            }}
            onMouseLeave={(e) => {
              if (helpVisible) { e.currentTarget.style.background = '#f5f5f5'; }
              else { e.currentTarget.style.borderColor = '#d4d4d4'; e.currentTarget.style.background = '#ffffff'; }
            }}
          >
            {helpVisible ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: 'rotate(90deg)' }}>
                  <circle cx="12" cy="12" r="10" /><path d="M15 9l-6 3 6 3" />
                </svg>
                <span>Hide Guide</span>
              </>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#848d9a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Portal-rendered Dropdown Menu - renders outside DOM hierarchy to avoid overflow clipping */}
      {dropdown.isOpen && dropdown.userId && dropdown.position && createPortal(
        <div
          ref={portalDropdownRef}
          className="users-portal-dropdown-menu"
          style={{
            position: 'fixed',
            top: dropdown.position.openUpward ? 'auto' : `${dropdown.position.top}px`,
            bottom: dropdown.position.openUpward ? `${window.innerHeight - dropdown.position.top + 4}px` : 'auto',
            left: `${dropdown.position.left}px`,
            transform: 'translateX(-100%)',
          }}
        >
          {(() => {
            const activeUser = filteredUsers.find(u => u.userId.toString() === dropdown.userId);
            if (!activeUser) return null;
            const adStatus = getADInvitationStatus(activeUser);
            const isExternal = isUserExternal(activeUser);
            return (
              <>
                <button
                  onClick={() => handleViewDetails(activeUser)}
                  className="users-portal-dropdown-item"
                  type="button"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  View Details
                </button>
                <button
                  onClick={() => handleEdit(activeUser)}
                  className="users-portal-dropdown-item"
                  type="button"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  Edit
                </button>
                
                {/* AD Invitation actions for external users */}
                {isExternal && selectedUserType === 'company' && (
                  <>
                    {adStatus === 'not_sent' && (
                      <button
                        onClick={() => handleSendInvitation(activeUser)}
                        className="users-portal-dropdown-item ad-action"
                        type="button"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                        Send AD Invitation
                      </button>
                    )}
                    {(adStatus === 'sent' || adStatus === 'pending') && (
                      <button
                        onClick={() => handleSyncInvitationStatus(activeUser)}
                        className="users-portal-dropdown-item ad-action"
                        type="button"
                        disabled={syncingStatus.has(activeUser.userEmail)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                        {syncingStatus.has(activeUser.userEmail) ? 'Refreshing...' : 'Refresh Status'}
                      </button>
                    )}
                    {(adStatus === 'sent' || adStatus === 'pending' || adStatus === 'expired') && (
                      <button
                        onClick={() => handleResendInvitation(activeUser)}
                        className="users-portal-dropdown-item ad-action"
                        type="button"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                        Resend Invitation
                      </button>
                    )}
                  </>
                )}
                
                <div className="users-portal-dropdown-divider"></div>
                <button
                  onClick={() => handleDelete(activeUser)}
                  className="users-portal-dropdown-item delete"
                  type="button"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  Delete
                </button>
              </>
            );
          })()}
        </div>,
        document.body
      )}

      {/* View Details Modal */}
      {viewDetailsModal.user && (
        <UserDetailsModal
          user={viewDetailsModal.user}
          isOpen={viewDetailsModal.isOpen}
          onClose={handleCloseViewDetails}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isExternal={isUserExternal(viewDetailsModal.user)}
          adStatus={getADInvitationStatus(viewDetailsModal.user)}
          onSendInvitation={handleSendInvitation}
          onResendInvitation={handleResendInvitation}
          onSyncInvitationStatus={handleSyncInvitationStatus}
          isSyncing={viewDetailsModal.user ? syncingStatus.has(viewDetailsModal.user.userEmail) : false}
        />
      )}

      {/* Delete Confirmation Modal - Updated with guest user info */}
      <UserDeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        userName={deleteModal.user?.userName || ''}
        isADGuest={deleteModal.user ? isADGuestUser(deleteModal.user) : false}
        isDeleting={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};